import { useLayoutEffect, useMemo, useRef, useState } from "react"

import ContractCard from "../ContractCard"
import useContractSectionCards from "../../../../hooks/useContractSectionCards"
import { useTranslation } from "react-i18next"

const ContractSectionCards = ({
  sections,
  sectionStatus,
  onSectionStatusChange = () => {},
  onShowSummary,
  progressPercentage,
  activeSectionIndex = 0,
  onActiveSectionChange,
  withoutEdit = false,
}) => {
  const { i18n, t } = useTranslation()
  const currentLang = i18n.language

  const wrapperRef = useRef(null)
  const containerRef = useRef(null)
  const listRef = useRef(null)
  const itemRefs = useRef([])
  const titleRef = useRef(null)
  const counterRef = useRef(null)

  const [counterH, setCounterH] = useState(0)

  useLayoutEffect(() => {
    const measure = () => {
      if (counterRef.current) {
        const r = counterRef.current.getBoundingClientRect()
        const my =
          parseFloat(getComputedStyle(counterRef.current).marginTop || "0") +
          parseFloat(getComputedStyle(counterRef.current).marginBottom || "0")
        setCounterH(r.height + my)
      } else {
        setCounterH(0)
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (wrapperRef.current) ro.observe(wrapperRef.current)
    window.addEventListener("resize", measure)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [currentLang, withoutEdit])

  const {
    isActive,
    translateY,
    centers,
    spacerH,
    answeredCount,
    handleCardClick,
    getDotColor,
  } = useContractSectionCards({
    sections,
    sectionStatus,
    activeSectionIndex,
    onActiveSectionChange,
    containerRef,
    listRef,
    itemRefs,
    wrapperRef,
  })

  const hasSections = Array.isArray(sections) && sections.length > 0

  const safeSpacerH = useMemo(() => {
    const n = Number(spacerH)
    if (!Number.isFinite(n) || n < 0) return 0
    return Math.min(n, 320)
  }, [spacerH])

  const safeTranslateY = useMemo(() => {
    const n = Number(spacerH)
    if (!Number.isFinite(n) || n > 2000) return 0
    return translateY
  }, [translateY, spacerH])

  const handleSectionStatusChange = (sectionIndex, status) =>
    onSectionStatusChange(sectionIndex, status)
  const handleEdit = (sectionIndex) => onSectionStatusChange(sectionIndex, null)

  if (!hasSections) return null

  return (
    <div
      ref={wrapperRef}
      className="flex flex-col h-full min-h-0 select-none"
      style={{ overscrollBehavior: "contain", touchAction: "none" }}
    >
      <h2
        ref={titleRef}
        className="text-[36px] font-extrabold text-dark-default  relative z-30 bg-light-default"
      >
        {t("contract.whatItMeans")}
      </h2>

      <div className="flex min-w-0 h-full">
        {/* Left column — timeline + dots */}
        <div className="w-12 h-full mr-2 flex flex-col relative">
          <div className="sticky top-0 flex-1 z-0">
            <div className="relative h-[95%] w-full flex justify-center overflow-visible">
              <div
                className="absolute left-1/2 -translate-x-1/2 w-1.5 rounded-full bg-dark-default z-0"
                style={{
                  top: 0,
                  bottom: withoutEdit ? 0 : counterH + 12,
                }}
              />
              <div className="pointer-events-none absolute inset-0 z-10">
                {sections.map((section, i) => {
                  const y = (centers[i] ?? 0) - safeTranslateY
                  return (
                    <div
                      key={`dot-${i}`}
                      className={`w-10 h-10 rounded-full border-[6px] border-dark-default ${getDotColor(
                        section,
                        i
                      )}
                                  absolute left-1/2 -translate-x-1/2
                                  transition-all duration-200 ease-out will-change-[top,transform]`}
                      style={{ top: y, transform: "translate(-50%, -50%)" }}
                      onClick={() => handleCardClick?.(i)}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right column — list of cards */}
        <div className="flex-1 min-w-0 relative z-0">
          <div
            ref={containerRef}
            className="cards-scroll relative h-full overflow-y-visible overflow-x-visible isolate"
            style={{ paddingBottom: 0, scrollPaddingBottom: 0 }}
          >
            <div
              ref={listRef}
              className="relative will-change-transform transition-transform duration-200 ease-out"
              style={{ transform: `translateY(${-safeTranslateY}px)` }}
            >
              <div style={{ height: safeSpacerH }} aria-hidden />
              <div className="py-5">
                {sections.map((section, index) => (
                  <ContractCard
                    key={section.id ?? index}
                    section={section}
                    index={index}
                    currentLang={currentLang}
                    active={isActive(index)}
                    status={sectionStatus[index]}
                    onSetStatus={handleSectionStatusChange}
                    onEdit={handleEdit}
                    onClick={handleCardClick}
                    setRef={(el) => (itemRefs.current[index] = el)}
                    withoutEdit={withoutEdit}
                  />
                ))}
              </div>
              <div style={{ height: safeSpacerH }} aria-hidden />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar (counter + button) */}
      <div className="summary-bar sticky bottom-0 inset-x-0 z-[60] pointer-events-none bg-light-default">
        <div className="flex items-center justify-between pointer-events-auto bg-light-default pl-3 gap-[52px] h-[48px]">
          {withoutEdit ? (
            <div className="w-[20px]" />
          ) : (
            <div ref={counterRef} className="py-2 rounded-md">
              <span className="block text-center text-xs font-semibold text-dark-default">
                {answeredCount}/{sections.length}
              </span>
            </div>
          )}
          {progressPercentage === 100 && (
            <button
              onClick={onShowSummary}
              className="flex-1 bg-success-default hover:bg-success-t-lighten-1 text-light-default px-6 py-3 rounded-xl font-semibold transition-colors duration-150 shadow-sm"
            >
              {t("contract.showSummary")}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .cards-scroll {
            padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 72px) !important;
            scroll-padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 72px) !important;
          }
          .summary-bar { bottom: 0 !important; }
        }
        @media (min-width: 769px) {
          .summary-bar { bottom: 0 !important; }
        }
      `}</style>
    </div>
  )
}

export default ContractSectionCards
